import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';

import { PreNominaMonitorService } from 'src/app/shared/services/prenomina-monitor.service';

/**
 * Indicador compacto de pre-nómina (o liquidación de contratos) en ejecución.
 * Destinado a mostrarse en el topbar del portal Empresas.
 * Muestra el avance como un anillo circular 0-100%.
 * Se renderiza SOLO cuando activo === true; de lo contrario no ocupa espacio.
 *
 * El texto que ve el cliente depende de `prenomina.esLiquidacionContrato`
 * (dato que llega del backend, calculado a partir del Indicativo real de la
 * fila en ProcessPre_Nomina): cuando es true se trata de un retiro de empleado
 * que está generando su paz y salvo, y el cliente NUNCA debe ver la palabra
 * "nómina". En cualquier otro caso se mantienen, sin cambios, los textos de
 * pre-nómina que ya existían.
 */
@Component({
    selector: 'app-prenomina-global-indicator',
    standalone: true,
    imports: [CommonModule, TooltipModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (monitor.activePreNomina(); as prenomina) {
            @if (prenomina.activo) {
                <div
                    class="prenomina-indicator"
                    role="status"
                    aria-live="polite"
                    [pTooltip]="tooltipText(prenomina.nombreConvenio, prenomina.agreement, prenomina.porcentaje, prenomina.esLiquidacionContrato)"
                    tooltipPosition="bottom"
                >
                    <!-- Anillo de avance circular -->
                    @if (prenomina.porcentaje != null) {
                        <div
                            class="prenomina-ring"
                            role="progressbar"
                            [attr.aria-valuenow]="prenomina.porcentaje"
                            aria-valuemin="0"
                            aria-valuemax="100"
                            [attr.aria-label]="progressAriaLabel(prenomina.esLiquidacionContrato)"
                        >
                            <svg viewBox="0 0 36 36" class="prenomina-ring__svg">
                                <circle class="prenomina-ring__bg" cx="18" cy="18" r="15.5"></circle>
                                <circle
                                    class="prenomina-ring__fill"
                                    cx="18"
                                    cy="18"
                                    r="15.5"
                                    [attr.stroke-dasharray]="circumference"
                                    [attr.stroke-dashoffset]="dashOffset(prenomina.porcentaje)"
                                ></circle>
                            </svg>
                            <span class="prenomina-ring__pct">{{ prenomina.porcentaje }}%</span>
                        </div>
                    } @else {
                        <span class="prenomina-ring prenomina-ring--indeterminate pi pi-spin pi-spinner" aria-hidden="true"></span>
                    }

                    <div class="prenomina-indicator__content">
                        <span class="prenomina-indicator__label">{{ labelText(prenomina.esLiquidacionContrato) }}</span>
                        @if (prenomina.nombreConvenio || prenomina.agreement) {
                            <span class="prenomina-indicator__convenio">
                                {{ prenomina.nombreConvenio ?? prenomina.agreement }}
                            </span>
                        }
                    </div>
                </div>
            }
        }
    `,
    styles: [
        `
        .prenomina-indicator {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 4px 10px;
            border-radius: 6px;
            background: rgba(var(--primary-color-rgb, 59, 130, 246), 0.08);
            border: 1px solid rgba(var(--primary-color-rgb, 59, 130, 246), 0.2);
            max-width: 240px;
            cursor: default;
        }

        .prenomina-indicator__content {
            display: flex;
            flex-direction: column;
            gap: 2px;
            min-width: 0;
        }

        .prenomina-indicator__label {
            font-size: 11px;
            font-weight: 600;
            color: var(--primary-color, #3b82f6);
            white-space: nowrap;
            line-height: 1.2;
        }

        .prenomina-indicator__convenio {
            font-size: 10px;
            color: var(--text-color-secondary, #6b7280);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            line-height: 1.2;
        }

        /* ── Anillo circular de avance ── */
        .prenomina-ring {
            position: relative;
            width: 34px;
            height: 34px;
            flex-shrink: 0;
        }

        .prenomina-ring--indeterminate {
            display: flex;
            align-items: center;
            justify-content: center;
            color: var(--primary-color, #3b82f6);
            font-size: 16px;
        }

        .prenomina-ring__svg {
            width: 100%;
            height: 100%;
            /* Inicia el arco en la parte superior */
            transform: rotate(-90deg);
        }

        .prenomina-ring__bg {
            fill: none;
            stroke: rgba(var(--primary-color-rgb, 59, 130, 246), 0.18);
            stroke-width: 3.5;
        }

        .prenomina-ring__fill {
            fill: none;
            stroke: var(--primary-color, #3b82f6);
            stroke-width: 3.5;
            stroke-linecap: round;
            transition: stroke-dashoffset 0.4s ease;
        }

        .prenomina-ring__pct {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 9px;
            font-weight: 700;
            color: var(--primary-color, #3b82f6);
            line-height: 1;
        }
        `
    ]
})
export class PrenominaGlobalIndicatorComponent {
    readonly monitor = inject(PreNominaMonitorService);

    /** Radio usado en el SVG del anillo. */
    private readonly radius = 15.5;
    /** Circunferencia del anillo: 2·π·r. */
    readonly circumference = 2 * Math.PI * this.radius;

    /** Calcula el offset del trazo para representar el porcentaje dado (0-100). */
    dashOffset(porcentaje: number): number {
        const pct = Math.min(100, Math.max(0, porcentaje));
        return this.circumference * (1 - pct / 100);
    }

    /**
     * Etiqueta corta del indicador. "Generando paz y salvo" cuando el proceso es
     * una liquidación de contratos (retiro de empleado); en cualquier otro caso,
     * exactamente el texto de pre-nómina de siempre.
     */
    labelText(esLiquidacionContrato: boolean): string {
        return esLiquidacionContrato ? 'Generando paz y salvo' : 'Pre-nómina en ejecución';
    }

    /** aria-label del anillo de progreso, coherente con {@link labelText}. */
    progressAriaLabel(esLiquidacionContrato: boolean): string {
        return esLiquidacionContrato ? 'Progreso del paz y salvo' : 'Progreso de pre-nómina';
    }

    tooltipText(
        nombreConvenio: string | null,
        agreement: string | null,
        porcentaje: number | null,
        esLiquidacionContrato: boolean
    ): string {
        const convenio = nombreConvenio ?? agreement;
        const avance = porcentaje != null ? ` (${porcentaje}%)` : '';
        const accion = this.labelText(esLiquidacionContrato);
        return convenio
            ? `${accion} — Convenio ${convenio}${avance}`
            : `${accion}${avance}`;
    }
}
